import { Brand } from '../../shared'
import { JobStatus } from '../order'

/**
 * Whether a rebalance move should update Phoenix (order service + user events)
 * rather than Laravel. Migrated Lyka customers and Tyga (Phoenix-native, never
 * on Laravel) both take the Phoenix path.
 */
export function orderUsesPhoenixRebalancePath(input: {
  isCustomerMigrated: boolean
  brand: Brand
}): boolean {
  return input.isCustomerMigrated || input.brand === Brand.TYGA
}

/**
 * Order/box fulfilment statuses from queued onwards — the order has entered
 * the packing pipeline and routing metadata (address, postcode, delivery date,
 * slot) should no longer be changed or have cutoffs re-resolved.
 */
export const FULFILMENT_STARTED_STATUSES: JobStatus[] = [
  JobStatus.QUEUED,
  JobStatus.IN_PROGRESS,
  JobStatus.PACKED,
  JobStatus.PALLETISED,
  JobStatus.DELIVERED,
]

/**
 * The only fulfilment statuses whose make_date a rebalance may move.
 *
 * Deliberately a whitelist: an order is movable because we know its boxes have
 * not been built yet, not because its status is absent from a list of bad ones.
 * Both ends of the rebalance read this — the candidate query in the order
 * service and the move validation in the orchestration workflow — so a stale
 * candidate list cannot post an id the write side would accept.
 */
export const REBALANCE_MOVABLE_FULFILMENT_STATUSES: JobStatus[] = [
  JobStatus.PENDING,
  JobStatus.DEPRIORITISED,
  JobStatus.ON_HOLD,
  JobStatus.BACKLOG,
]

/**
 * Job statuses (at box level) that pin a box to its make date. From queued
 * onwards the box is claimable at a boxing station or already built, and a
 * paused/unpaused box was interrupted mid-pack, so it may be partially built.
 * Moving the fulfilment's make_date under any of these strands the box on the
 * original day. Deliberately excludes `cancelled`: replaced boxes stay on the
 * fulfilment as inert rows and must not freeze the live boxes' schedule.
 */
export const MAKE_DATE_BLOCKING_JOB_STATUSES: string[] = [
  JobStatus.QUEUED,
  JobStatus.IN_PROGRESS,
  JobStatus.PACKED,
  JobStatus.PALLETISED,
  JobStatus.PAUSED,
  JobStatus.UNPAUSED,
]

/**
 * Job statuses (at box level) that indicate fulfilment has started
 * and the order can no longer be safely interrupted by placing it on hold.
 */
export const BLOCKED_JOB_STATUSES: string[] = [
  JobStatus.IN_PROGRESS, // Box has started packing
  JobStatus.PACKED, // Box is already packed
  JobStatus.PALLETISED, // Box is palletised,
]

/**
 * Job statuses that must not be re-routed to a different boxing station or
 * silently moved back into the backlog. Used by the boxing-station SKU
 * rebalance and manual assign/unassign paths to prevent OPS-3021 double-packs.
 *
 * - `in_progress`: claimed by a packer at a specific station; reassignment
 *   would let a second packer claim the same box.
 * - `packed`, `palletised`: terminal; must never go backwards
 * - `cancelled`: terminal; re-routing is a data-integrity issue
 */
export const NON_REASSIGNABLE_JOB_STATUSES: string[] = [
  JobStatus.IN_PROGRESS,
  JobStatus.PACKED,
  JobStatus.PALLETISED,
  JobStatus.CANCELLED,
]

/**
 * Job statuses that should NOT be counted toward today's pack workload.
 * Used to filter daily Box Management views (Box Tracker list and the
 * planned/packed progress denominator) so cancelled or held jobs don't
 * inflate the day's totals.
 */
export const NON_PACKABLE_TODAY_JOB_STATUSES: string[] = [
  JobStatus.CANCELLED,
  JobStatus.ON_HOLD,
]

/**
 * Job statuses eligible for the missing-dispatch-cutoff sweeper.
 *
 * Paused and held boxes are in scope, not just pre-pack ones: a null cutoff
 * reads as "no 5pm cutoff" when a make date moves, and sorts the box last once
 * it is back in backlog. Writing it cannot disturb packing, because station
 * selection only ever picks up `backlog` jobs. Excludes the active queue and
 * terminal states.
 */
export const MISSING_DISPATCH_CUTOFF_SWEEP_JOB_STATUSES: string[] = [
  JobStatus.ON_HOLD,
  JobStatus.BACKLOG,
  JobStatus.PENDING,
  JobStatus.DEPRIORITISED,
  JobStatus.PAUSED,
  JobStatus.UNPAUSED,
]
